import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { ClientConfigItemsTable } from './ClientConfigItemsTable';
import type { HierarchicalConfigItem } from '../types/phase1e';

/**
 * Discovery grid modal (engagement card icon, top navigation).
 *
 * Phase A of docs/DISCOVERY_GRID_REDESIGN_SPEC.md: the modal used to fetch the
 * flattened `/progress-grid` items and re-shape them into fake config items with
 * no question list, which made the table drop every unanswered question (defect
 * D4). It now loads the company's real config items and engagements and renders
 * exactly what the client detail tab renders. Phase B replaces both with the
 * single discovery-grid endpoint.
 */

interface ProgressGridModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId?: string;
  companyName?: string;
  engagementId?: string;
  accessToken: string;
  currentUser: any;
  spaceId?: string | null;
  spaceType?: 'global' | 'space';
  /** Select this cell (and open its drawer) once the grid loads. */
  initialCellId?: string | null;
}

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

export function ProgressGridModal({
  isOpen,
  onClose,
  companyId,
  companyName,
  engagementId,
  accessToken,
  currentUser,
  spaceId,
  spaceType,
  initialCellId,
}: ProgressGridModalProps) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<HierarchicalConfigItem[]>([]);
  const [sows, setSows] = useState<any[]>([]);
  const [spaces, setSpaces] = useState<{ id: string; name: string }[]>([]);

  const loadGridData = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    const headers = { Authorization: `Bearer ${accessToken}` };
    try {
      setLoading(true);

      const itemsUrl = new URL(`${BASE}/companies/${companyId}/config-items`);
      if (spaceId && spaceType === 'space') itemsUrl.searchParams.append('spaceId', spaceId);

      let [itemsRes, sowsRes] = await Promise.all([
        fetch(itemsUrl.toString(), { headers }),
        // Engagements drive the focused-engagement resolution and the work-type artifact
        // cells. Client users may not be allowed to list them; the grid still renders.
        fetch(`${BASE}/companies/${companyId}/sows`, { headers }).catch(() => null),
      ]);

      if (itemsRes.status === 403) {
        // The company route is firm-only; client users read their items through the
        // company-scoped config-items route the client dashboard already uses.
        const clientUrl = new URL(`${BASE}/config-items/${companyId}`);
        if (spaceId && spaceType === 'space') clientUrl.searchParams.append('spaceId', spaceId);
        itemsRes = await fetch(clientUrl.toString(), { headers });
      }
      if (!itemsRes.ok) {
        throw new Error(`Failed to load config items: ${itemsRes.statusText}`);
      }
      const itemsData = await itemsRes.json();
      const mapped: HierarchicalConfigItem[] = (itemsData.items || []).map((item: any) => ({
        ...item,
        id: item.id || item.itemId || item.configItemId,
      })).filter((item: any) => !!item.id);
      setItems(mapped);

      if (sowsRes && sowsRes.ok) {
        const sowsData = await sowsRes.json().catch(() => ({}));
        setSows(Array.isArray(sowsData.sows) ? sowsData.sows : []);
      } else {
        setSows([]);
      }

      // Spaces come from the company summary in the detail view; the modal has no space
      // filter (it is either global or already scoped by spaceId), so the list stays empty.
      setSpaces([]);
      console.log('[ProgressGridModal] Loaded config items:', mapped.length);
    } catch (error) {
      console.error('[ProgressGridModal] Error loading grid data:', error);
      toast.error('Failed to load the discovery grid');
    } finally {
      setLoading(false);
    }
  }, [companyId, accessToken, spaceId, spaceType]);

  useEffect(() => {
    if (isOpen) {
      loadGridData();
    }
  }, [isOpen, loadGridData]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[90vw] !w-[90vw] !h-[90vh] max-h-[90vh] p-0 flex flex-col gap-0 overflow-hidden bg-white shadow-2xl border-2 border-gray-200">
        <DialogTitle className="sr-only">Discovery Grid</DialogTitle>
        <DialogDescription className="sr-only">
          Every question and artifact this engagement needs before build, one cell each.
        </DialogDescription>
        <div className="flex-1 overflow-hidden flex flex-col">
          {loading && items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 gap-2 text-sm">
              <Loader2 className="size-4 animate-spin" />
              Loading the discovery grid…
            </div>
          ) : (
            <ClientConfigItemsTable
              items={items}
              spaces={spaces}
              companyId={companyId || ''}
              companyName={companyName || ''}
              accessToken={accessToken}
              currentUser={currentUser}
              onUpdate={loadGridData}
              initialOpenItemId={undefined}
              initialCellId={initialCellId ?? null}
              isModal={true}
              onCloseModal={onClose}
              sows={sows}
              engagementId={engagementId ?? null}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
